import React, { FC, useEffect, useState } from 'react';
import { Button, Paper, Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';

import { ITimecardPeriodDTO } from 'src/api/dtos';
import { TimecardDetailsDrawer } from 'src/features/timecards';
import { EmptyState, ServerError500Icon, Tab, Tabs, TimecardIcon } from 'src/shared/components';
import { PeriodTypeEnums } from 'src/shared/enums';
import { useThunkDispatch } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { fetchTimecardDetails, fetchTimecardPeriods, fetchTimecardSummary } from 'src/store/timecard/timecard.actions';
import { RootState } from 'src/store/types';

import { TimecardMetrics } from './TimecardMetrics';

export const TimecardCard: FC = () => {
  const dispatch = useThunkDispatch();
  const { t } = useTranslate();
  const [currentTab, setCurrentTab] = useState<PeriodTypeEnums>(PeriodTypeEnums.WEEKLY);
  const [openTimecardDetails, setOpenTimecardDetails] = useState<boolean>(false);
  const { currentSummary, details, periods, summary, error, loadingDetails, loadingSummary } = useSelector(
    (state: RootState) => state.timeCard,
  );
  const subtitle = currentTab === PeriodTypeEnums.WEEKLY ? t('global.currentWeek') : t('timecards.currentPayPeriod');

  useEffect(() => {
    dispatch(fetchTimecardPeriods(currentTab));
  }, [currentTab]);

  const handleTabChange = (tab: PeriodTypeEnums) => {
    setCurrentTab(tab);
  };

  const handleSummaryChange = (newPeriod: ITimecardPeriodDTO) => {
    dispatch(fetchTimecardDetails(newPeriod.periodId));
    dispatch(fetchTimecardSummary(newPeriod.periodId));
  };

  const handleClose = () => {
    dispatch(fetchTimecardPeriods(currentTab));
    setOpenTimecardDetails(false);
  };

  if ((!periods || !summary) && !error) {
    return <Skeleton className="rounded border" variant="rectangular" animation="wave" height={320} />;
  }

  if (error) {
    return (
      <Paper className="h-100 p-4">
        <h4 className="pb-0 mb-0">{t('timecards.timecard')}</h4>
        <EmptyState className="m-0" message={t('global.oopsAnErrorOccurred')} icon={<ServerError500Icon />} />
      </Paper>
    );
  }

  return (
    <Paper id="timecard-card" className="h-100 p-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2">
        <h4 className="pb-0 mb-0">{t('timecards.timecard')}</h4>
        <Button
          id="view-timecard-details"
          color="secondary"
          startIcon={<TimecardIcon size={20} />}
          onClick={() => setOpenTimecardDetails(true)}
        >
          {t('timecards.viewTimecardDetails')}
        </Button>
      </div>
      <div>
        <Tabs value={currentTab} onClick={handleTabChange}>
          <Tab value={PeriodTypeEnums.WEEKLY} label={t('timecards.weekly')} />
          <Tab value={PeriodTypeEnums.PAY_PERIOD} label={t('timecards.payPeriod')} />
        </Tabs>
        <TimecardMetrics currentSummary={currentSummary} subtitle={subtitle} />
      </div>
      {summary && (
        <TimecardDetailsDrawer
          details={details}
          summary={summary}
          periods={periods}
          periodType={currentTab}
          open={openTimecardDetails}
          loadingSummary={loadingSummary}
          loadingDetails={loadingDetails}
          onClose={handleClose}
          onTabChange={handleTabChange}
          onDateRangeChance={handleSummaryChange}
        />
      )}
    </Paper>
  );
};
